/**
 * AppMode Service
 *
 * Detects whether the app is running in "App/API mode" (opened via Android/iOS WebView
 * or any client that passes ?arkaApp=<contactId>) or "Web mode" (direct browser access).
 *
 * URL format for App mode:  ?arkaApp=<contactId>
 * Example:                  https://portal.com/?arkaApp=1146
 *
 * Web mode (no param):      https://portal.com/
 *
 * Mode persists for the entire browser/WebView session via sessionStorage so that
 * SPA navigation within the same session does not lose the active mode.
 */

const SESSION_MODE_KEY = 'arkaAppMode';
const SESSION_CONTACT_KEY = 'arkaContactId';
const SESSION_LAT_KEY = 'arkaLat';
const SESSION_LNG_KEY = 'arkaLng';
const SESSION_ZOOM_KEY = 'arkaZoom';
const SESSION_TOKEN_KEY = 'arkaToken';

class AppMode {
    constructor() {
        this._initialized = false;
        this._isAppMode = false;
        this._contactId = null;
        this._lat = null;
        this._lng = null;
        this._zoom = null;
        this._authToken = null;
    }

    initialize() {
        if (this._initialized) return;

        const params = new URLSearchParams(window.location.search);
        const arkaParam = params.get('arkaApp');

        if (arkaParam !== null) {
            // arkaApp param present in URL → activate App/API mode
            this._isAppMode = true;
            this._contactId = arkaParam || null;
            sessionStorage.setItem(SESSION_MODE_KEY, 'true');
            if (this._contactId) {
                sessionStorage.setItem(SESSION_CONTACT_KEY, this._contactId);
            }

            // Read optional lat / lng / zoom params passed from ArkaGo
            const latParam   = params.get('lat');
            const lngParam   = params.get('lng');
            const zoomParam  = params.get('zoom');
            const tokenParam = params.get('arkaToken');

            if (latParam   !== null) { this._lat       = parseFloat(latParam);    sessionStorage.setItem(SESSION_LAT_KEY,   latParam); }
            if (lngParam   !== null) { this._lng       = parseFloat(lngParam);    sessionStorage.setItem(SESSION_LNG_KEY,   lngParam); }
            if (zoomParam  !== null) { this._zoom      = parseInt(zoomParam, 10); sessionStorage.setItem(SESSION_ZOOM_KEY,  zoomParam); }
            if (tokenParam !== null) { this._authToken = tokenParam;              sessionStorage.setItem(SESSION_TOKEN_KEY, tokenParam); }
        } else {
            // No ?arkaApp param in URL → always web mode, no exceptions.
            // _isAppMode is NEVER read from sessionStorage — only the URL param can
            // activate app mode. This prevents stale sessions from bleeding over into
            // the normal web portal (e.g. developer navigating from a ?arkaApp URL to
            // localhost without the param, or same-origin SPA nav that strips the param).
            this._isAppMode = false;
            this._contactId = null;

            // Auxiliary params (token, lat/lng/zoom) may still be present in
            // sessionStorage from SPA navigation within the same app-mode page load.
            // We keep them here so that a Vue router navigation that strips the query
            // string doesn't lose the token mid-session.
            const storedToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
            const storedLat   = sessionStorage.getItem(SESSION_LAT_KEY);
            const storedLng   = sessionStorage.getItem(SESSION_LNG_KEY);
            const storedZoom  = sessionStorage.getItem(SESSION_ZOOM_KEY);
            if (storedToken) this._authToken = storedToken;
            if (storedLat)   this._lat       = parseFloat(storedLat);
            if (storedLng)   this._lng       = parseFloat(storedLng);
            if (storedZoom)  this._zoom      = parseInt(storedZoom, 10);

            // Clean up the mode/contact keys so they never accidentally re-activate.
            sessionStorage.removeItem(SESSION_MODE_KEY);
            sessionStorage.removeItem(SESSION_CONTACT_KEY);
        }

        // Expose contactId globally so rnBridge.js can include it in postMessage payloads
        // without needing to import the appMode singleton
        if (this._isAppMode && this._contactId) {
            window.__arkaContactId = this._contactId;
        }

        this._initialized = true;
        console.log(
            `[AppMode] ${this._isAppMode ? '🚀 App/API mode' : '🌐 Web/localStorage mode'}` +
            (this._contactId ? ` | contactId: ${this._contactId}` : '') +
            (this._lat != null ? ` | lat: ${this._lat}, lng: ${this._lng}, zoom: ${this._zoom}` : '') +
            (this._authToken ? ' | arkaToken: ✅' : ' | arkaToken: ❌ (using hardcoded fallback)')
        );
    }

    /** Returns true when the app is opened via ?arkaApp param (WebView / app clients). */
    isAppMode() {
        if (!this._initialized) this.initialize();
        return this._isAppMode;
    }

    /** Returns true for normal direct-browser access (no arkaApp param). */
    isWebMode() {
        return !this.isAppMode();
    }

    /** The contactId extracted from the ?arkaApp=<contactId> URL param. */
    getContactId() {
        if (!this._initialized) this.initialize();
        return this._contactId;
    }

    /**
     * Auth token injected by ArkaGo via ?arkaToken=<token> URL param.
     * Returns null if not provided — callers should fall back to the hardcoded STUDIO_API_TOKEN.
     */
    getAuthToken() {
        if (!this._initialized) this.initialize();
        return this._authToken;
    }

    /** Latitude passed via ?lat= URL param from ArkaGo. Null if not provided. */
    getLat() {
        if (!this._initialized) this.initialize();
        return this._lat;
    }

    /** Longitude passed via ?lng= URL param from ArkaGo. Null if not provided. */
    getLng() {
        if (!this._initialized) this.initialize();
        return this._lng;
    }

    /** Zoom level passed via ?zoom= URL param from ArkaGo. Null if not provided. */
    getZoom() {
        if (!this._initialized) this.initialize();
        return this._zoom;
    }

    /**
     * Returns {lat, lng, zoom} from URL params if all three were provided, otherwise null.
     * Use this as the authoritative location override in app mode.
     */
    getLocationParams() {
        if (!this._initialized) this.initialize();
        if (this._lat != null && this._lng != null) {
            return {
                lat: this._lat,
                lng: this._lng,
                zoom: this._zoom ?? 20,
            };
        }
        return null;
    }

    /** Debug/test helper: clears the stored mode from the current session. */
    reset() {
        sessionStorage.removeItem(SESSION_MODE_KEY);
        sessionStorage.removeItem(SESSION_CONTACT_KEY);
        sessionStorage.removeItem(SESSION_LAT_KEY);
        sessionStorage.removeItem(SESSION_LNG_KEY);
        sessionStorage.removeItem(SESSION_ZOOM_KEY);
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        this._isAppMode = false;
        this._contactId = null;
        this._lat = null;
        this._lng = null;
        this._zoom = null;
        this._authToken = null;
        this._initialized = false;
    }
}

export default new AppMode();
